// Shared signal so a deliberate full-page redirect — e.g. clicking Upgrade and
// heading to Stripe checkout — doesn't trip the builder page's `beforeunload`
// "Leave site?" guard while a build is generating.
//
// The guard (app/project/[id]/page.tsx) exists to catch *unintended* leaves
// mid-build; an intentional billing redirect should pass through silently.
// Every checkout redirect goes through `redirectToBilling()` (or calls
// `markIntentionalNavigation()` right before navigating), and the guard reads
// `isIntentionalNavigation()` and stays quiet. Unrelated navigations (closing
// the tab, hitting back) never raise the flag, so genuine unsaved-work
// protection is untouched.

let intentional = false;
let resetTimer: ReturnType<typeof setTimeout> | null = null;

// Self-clear window. If the navigation is cancelled or never fires (bad URL, a
// fetch that rejects before we redirect), we must not leave the guard
// permanently disabled — so the flag auto-resets shortly after being raised.
const DEFAULT_TTL_MS = 5000;

/** Mark that the next navigation is a deliberate redirect, so the builder's
 *  `beforeunload` guard stays silent for it. Auto-clears after `ttlMs`. */
export function markIntentionalNavigation(ttlMs = DEFAULT_TTL_MS): void {
  intentional = true;
  if (resetTimer) clearTimeout(resetTimer);
  if (typeof window === "undefined") return;
  resetTimer = setTimeout(() => {
    intentional = false;
    resetTimer = null;
  }, ttlMs);
}

/** Drop the flag immediately (e.g. a redirect attempt aborted synchronously). */
export function clearIntentionalNavigation(): void {
  intentional = false;
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
}

export function isIntentionalNavigation(): boolean {
  return intentional;
}

/** Redirect the browser to a billing / Stripe checkout URL without tripping the
 *  builder page's `beforeunload` guard. Use this for every upgrade / top-up /
 *  trial checkout redirect instead of assigning `window.location` directly. */
export function redirectToBilling(url: string): void {
  markIntentionalNavigation();
  if (typeof window !== "undefined") {
    window.location.assign(url);
  }
}

/** Hand a `mailto:` link to the OS mail client without the builder's
 *  `beforeunload` guard asking "Leave site?".
 *
 *  Chrome dispatches `beforeunload` for a mailto: navigation even though the
 *  page is never unloaded (verified in Chromium and Chrome), so an email share
 *  from inside the workspace during a build raised the leave prompt. The event
 *  is dispatched synchronously inside the assignment, so the flag is up for
 *  that one statement only and then restored — never left raised for the
 *  redirect TTL, where it would also silence a genuine tab close. */
export function openMailComposer(mailtoUrl: string): void {
  if (typeof window === "undefined") return;
  const previously = intentional;
  intentional = true;
  try {
    window.location.href = mailtoUrl;
  } finally {
    intentional = previously;
  }
}
